import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppButton } from "../form";
import { widgetService, type WidgetVersionSummary } from "../../services/widgetService";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  widgetId: string;
  onRestored?: () => void;
};

export default function WidgetVersionHistory({ widgetId, onRestored }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;
  const [versions, setVersions] = useState<WidgetVersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!widgetId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await widgetService.listVersions(widgetId);
      setVersions(res.content || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [widgetId]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const restore = async (versionId: string) => {
    setRestoringId(versionId);
    setError(null);
    try {
      await widgetService.restoreVersion(widgetId, versionId);
      await load();
      onRestored?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={styles.versionHistoryHeader}>
        <HistoryOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          {t("widgetVersion.title")}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={styles.versionHistorySubtitle}>
        {t("widgetVersion.subtitle")}
      </Typography>
      {error && <Alert severity="error" sx={styles.versionHistoryError}>{error}</Alert>}
      {loading ? (
        <Box sx={styles.versionHistoryLoading}>
          <CircularProgress size={22} />
        </Box>
      ) : versions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("widgetVersion.empty")}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {versions.map((v) => (
            <Stack
              key={v.id}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              gap={1}
              sx={styles.versionHistoryRow}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  v{v.versionNumber} — {v.widgetName || t("widget.title")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {v.savedAt ? new Date(v.savedAt).toLocaleString() : "—"}
                  {v.savedBy ? ` · ${v.savedBy}` : ""}
                </Typography>
              </Box>
              <AppButton
                size="small"
                variant="outlined"
                disabled={restoringId === v.id}
                onClick={() => restore(v.id)}
              >
                {restoringId === v.id ? t("common.loading") : t("widgetVersion.restore")}
              </AppButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
